import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import moment from "moment";
//Import from project
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import {
  CREATE_PERFORMANCE_TYPE,
  UPDATE_PERFORMANCE_TYPE,
} from "../../Schema/PerformanceType";
import { SelectDatePicker } from "../Include/SelectDatePicker/SelectDatePicker";

export default function PerformanceTypeForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createPerformanceType] = useMutation(CREATE_PERFORMANCE_TYPE, {
    onCompleted: ({ createPerformanceType }) => {
      // console.log("createPerformanceType :", createPerformanceType);
      setLoading(false);
      if (createPerformanceType?.isSuccess === true) {
        setAlert(true, "success", createPerformanceType?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createPerformanceType?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  // console.log("createPerformanceType :", createPerformanceType({}));

  const [updatePerformanceType] = useMutation(UPDATE_PERFORMANCE_TYPE, {
    onCompleted: ({ updatePerformanceType }) => {
      setLoading(false);
      if (updatePerformanceType?.isSuccess === true) {
        setAlert(true, "success", updatePerformanceType?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updatePerformanceType?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    khName: Yup.string().required(t("required")),
    enName: Yup.string().required(t("required")),
    date: Yup.string().required(t("required")),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      date: moment(),
      khName: "",
      enName: "",
      remark: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createPerformanceType({
          variables: {
            input: {
              ...values,
              date: moment(values?.date).format("YYYY-MM-DD"),
            },
          },
        });
      } else {
        updatePerformanceType({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              date: moment(values?.date).format("YYYY-MM-DD"),
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  // console.log("editData::", editData);

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("date", editData?.date);
      setFieldValue("khName", editData?.khName);
      setFieldValue("enName", editData?.enName);
      setFieldValue("remark", editData?.remark ?? "");
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_performance_type")
            : t("modal_title_update_performance_type")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_khmer_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_khmer_name")}
                  {...getFieldProps("khName")}
                  error={Boolean(touched.khName && errors.khName)}
                  helperText={touched.khName && errors.khName}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_english_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_english_name")}
                  {...getFieldProps("enName")}
                  error={Boolean(touched.enName && errors.enName)}
                  helperText={touched.enName && errors.enName}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.date}
                  onChange={(val) => setFieldValue("date", val)}
                />
                {!!errors.date && touched.date && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.date}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  rows={2}
                  multiline
                  fullWidth
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                  error={Boolean(touched.remark && errors.remark)}
                  helperText={touched.remark && errors.remark}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
